import { SOMETHING_WENT_WRONG } from "constants/apiCallError";
import { isManualBooksGlobalStatus } from "constants/booksGlobalStatus";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  useUpdateTaskAssigneeAdminMutation,
  useUpdateTaskAssigneeProviderMutation,
} from "store/apis/serviceTeam";
import { taskApis } from "store/apis/task";
import { BooksGlobalStatus } from "types/Models/bookkeepingTracker";
import { BackendError } from "types/utils/error";

export type BooksGlobalStatusUpdate = {
  pendingStatus: BooksGlobalStatus | null;
  requestStatusChange: (status: BooksGlobalStatus) => void;
  cancelStatusChange: () => void;
  confirmStatusChange: () => Promise<void>;
  isUpdating: boolean;
};

// One confirmed backend call sets the Books status and moves the workflow, for every page that edits it
export const useBooksGlobalStatusUpdate = (
  taskId: string
): BooksGlobalStatusUpdate => {
  const dispatch = useDispatch();
  const { alertToast, successToast } = useToast();
  const { isAdmin } = useRoleBasedView();
  const [pendingStatus, setPendingStatus] = useState<BooksGlobalStatus | null>(
    null
  );
  const [updateForAdmin, { isLoading: isUpdatingAdmin }] =
    useUpdateTaskAssigneeAdminMutation();
  const [updateForProvider, { isLoading: isUpdatingProvider }] =
    useUpdateTaskAssigneeProviderMutation();

  const requestStatusChange = (status: BooksGlobalStatus) => {
    if (isManualBooksGlobalStatus(status.key)) {
      setPendingStatus(status);
    }
  };

  const confirmStatusChange = async () => {
    if (!pendingStatus) return;
    const updateTaskAssignee = isAdmin ? updateForAdmin : updateForProvider;
    try {
      await updateTaskAssignee({
        taskId,
        books_global_status: pendingStatus.key,
      }).unwrap();
      // the workflow may have moved on the backend, so refresh task details and states too
      dispatch(taskApis.util.invalidateTags(["Tasks"]));
      successToast({ message: "Books status updated successfully!" });
      setPendingStatus(null);
    } catch (error) {
      // the dialog stays open so the change can be retried
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ?? SOMETHING_WENT_WRONG,
      });
    }
  };

  return {
    pendingStatus,
    requestStatusChange,
    cancelStatusChange: () => setPendingStatus(null),
    confirmStatusChange,
    isUpdating: isUpdatingAdmin || isUpdatingProvider,
  };
};
